import React from 'react'
import { observer } from 'mobx-react'
import Link from '../../models/Link'
import { computed, makeObservable, observable } from 'mobx';
import ApiClient from '../../api/ApiClient'
import { Button, ButtonGroup, Card, CardContent, CircularProgress, FormGroup, Grid, Input, InputAdornment, TextField } from '@mui/material'
import { NavigationListener } from './DStepper'
import { AcceptJsAuthData, BankAccountDataType, CreditCardDataType, loadAcceptJs } from '../../common/AuthNet'
import DialogManager from '../dialog-provider/DialogManager'
import ApiClientFactory from '../../api/ApiClientFactory'
// @ts-ignore
import * as cardValidator from 'card-validator'
import _ from 'lodash'
import AccountBox from '@mui/icons-material/AccountBox'
import AccountBalance from '@mui/icons-material/AccountBalance'
import CreditCard from '@mui/icons-material/CreditCard'
import CalendarToday from '@mui/icons-material/CalendarToday'
import Lock from '@mui/icons-material/Lock'
import LocationOn from '@mui/icons-material/LocationOn'
import DynamicIcon from '../DynamicIcon'
import moment from 'moment'
import { Autocomplete } from '@mui/lab'
import { v4 as uuidv4 } from 'uuid'
import {CyberSourceClient, CyberSourceKey, loadMicroform} from '../../common/CyberSource'
import { HomeOutlined } from '@mui/icons-material'
import {styled} from "@mui/system";
import PaymentMethodSelector from '../PaymentMethodSelector'

type Props = {
  url: string
  navigationListener: NavigationListener
  onChange: (id: number) => void
}

export type TokenizedData = {
  dataDescriptor: string
  dataValue: string
}

type AuthNetTokenizer = {
  type: 'Accept.js'
  authentication: {
    clientKey: string
    name: string
  }
}

type CyberSourceTokenizer = {
  type: 'CyberSource'
  authentication: {
    keyId: string
    der: {
      format: string
      algorithm: string
      publicKey: string
    }
    jwk: {
      kty: string
      use: string
      kid: string
      n: string
      e: string
    }
  }
}

type CyberSourceMicroformTokenizer = {
  type: 'CyberSourceMicroform'
}

type OtherTokenizer = {
  type: 'Other'
}

type PaymentMethodTokenizer = AuthNetTokenizer | CyberSourceTokenizer | OtherTokenizer | CyberSourceMicroformTokenizer

export type ApplePayRegularBilling = {
  label: string
  amount: string
  paymentTiming: 'recurring'
  recurringPaymentStartDate?: string
  recurringPaymentEndDate?: string
  recurringPaymentIntervalUnit?: 'year' | 'month' | 'day' | 'hour' | 'minute'
  recurringPaymentIntervalCount?: number
}

export type ApplePayRecurringPaymentRequest = {
  paymentDescription: string
  regularBilling: ApplePayRegularBilling
  managementURL: string
  billingAgreement?: string
  tokenNotificationURL?: string
}

export type ApplePayDeferredBilling = {
  label: string
  amount: string
  paymentTiming: 'deferred'
  deferredPaymentDate: string
}

export type ApplePayDeferredPaymentRequest = {
  paymentDescription: string
  deferredBilling: ApplePayDeferredBilling
  managementURL: string
  freeCancellationDate?: string
  freeCancellationDateTimeZone?: string
  billingAgreement?: string
  tokenNotificationURL?: string
}

export type ApplePayTokenizer = {
  countryCode: string
  currencyCode: string
  supportedNetworks: string[]
  merchantCapabilities: string[]
  postbackValidationURL: string
  recurringPaymentRequest?: ApplePayRecurringPaymentRequest
  deferredPaymentRequest?: ApplePayDeferredPaymentRequest
}

export type DigitalPaymentMethod = {
  type: 'applepay' | 'googlepay'
  label: string
  amount: number
  tokenizer: ApplePayTokenizer // will become a union when Google Pay is added
}

type NewPaymentMethodType = {
  uuid?: string
  name: string
  formType: string
  method: string
  tokenizer: PaymentMethodTokenizer
}

type SavedPaymentMethodType = {
  id: number
  displayDetails: {
    icon: string
    label: string
    subLabel: string
    removeLabel: string
    removeConfirmation: string
    removeRel: string
  }
}

type PaymentMethodData = {
  paymentMethodHeader: string
  paymentFormLocale: string
  savedPaymentMethods: SavedPaymentMethodType[]
  preferredPaymentMethod: number | null
  newPaymentMethodHeader: string
  newPaymentMethods: NewPaymentMethodType[]
  newDigitalMethods?: DigitalPaymentMethod[]
  postback: {
    label: string
    variant: 'contained' | 'outlined'
    icon: string
    postbackUrl: string
    postbackData: {
      save: boolean,
      type: string
      description: string
      last4: string
      method: string
      token: any
    }
    route: string
  }
  links: Link[]
}

const StyledInput = styled(Input)`
  input::placeholder {
    color: #757575;
    opacity: 1;
  }
`

const StyledTextField = styled(TextField)`
  input::placeholder {
    color: #757575;
    opacity: 1;
  }
`

const MICROFORM_STYLES = {
  input: {
    'font-size': '16px',
    'font-family': 'Roboto, Helvetica, Arial, sans-serif',
    'font-weight': '500',
    'color': 'rgba(0,0,0,0.87)',
    'padding': '6px 0 7px 0',
    'line-height': '1.4375em',
    //'box-sizing': 'border-box',
    'border': 'none',
    'border-bottom': '1px solid rgba(0,0,0,0.42)'
  },
  '::placeholder': {
    color: '#999',
    opacity: 1
  },
  ':focus': {
    color: 'rgba(0,0,0,0.87)',
    'outline': 'none',
    'border-bottom-color': 'rgba(0,0,0,0.87)'
  },
  'valid': { color: 'rgba(0,0,0,0.87)' },
  'invalid': { color: '#d32f2f' }
};

const DPaymentMethod = observer(class DPaymentMethod extends React.Component<Props> {
  private paymentMethodData?: PaymentMethodData;
  private error?: string;
  private loading = false;
  private preferredPaymentMethodRemoved = false;
  private microform: any;
  private fieldsLoaded = false;
  private showPaymentForm = false;
  private skipManualToken?: true;

  private cardData = {
    nameOnCard: '',
    cardNumber: '',
    expMonth: '',
    expYear: '',
    securityCode: '',
    zipCode: '',
  };

  private bankData = {
    nameOnAccount: '',
    routingNumber: '',
    accountNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
  };

  selectedNewPaymentMethodUuid?: string;

  constructor (props: Props) {
    super(props);

    makeObservable<DPaymentMethod, "paymentMethodData" | "error" | "loading" | "preferredPaymentMethodRemoved" | "cardData" | "bankData" | "microform" | "showPaymentForm" | "skipManualToken">(this, {
      paymentMethodData: observable,
      error: observable,
      loading: observable,
      preferredPaymentMethodRemoved: observable,
      cardData: observable,
      bankData: observable,
      selectedNewPaymentMethodUuid: observable,
      selectedNewPaymentMethod: computed,
      paymentMethodFormType: computed,
      preferredPaymentMethod: computed,
      microform: observable,
      showPaymentForm: observable,
      skipManualToken: observable
    });
  }

  get selectedNewPaymentMethod (): NewPaymentMethodType | undefined {
    return this.selectedNewPaymentMethodUuid
      ? this.paymentMethodData?.newPaymentMethods.find(pm => pm.uuid === this.selectedNewPaymentMethodUuid)
      : undefined
  }

  get paymentMethodFormType () {
    if (this.selectedNewPaymentMethod) {
      if (this.selectedNewPaymentMethod.method === 'credit' || this.selectedNewPaymentMethod.method === 'debit' || this.selectedNewPaymentMethod.method === 'hsafsa') {
        return 'card'
      } else if (this.selectedNewPaymentMethod.method === 'ach') {
        return 'bank'
      }
    }

    return undefined
  }

  get preferredPaymentMethod (): SavedPaymentMethodType | undefined {
    const paymentMethodData = this.paymentMethodData
    if (!this.preferredPaymentMethodRemoved && paymentMethodData && paymentMethodData.preferredPaymentMethod) {
      return _.find(paymentMethodData.savedPaymentMethods, pm => String(pm.id) === String(paymentMethodData.preferredPaymentMethod))
    }

    return undefined
  }

  componentDidMount (): void {
    if (!this.paymentMethodData) {
      this.loadPaymentMethodData()
    }

    this.props.navigationListener.addListener(this.onStepNavigation)
  }

  componentWillUnmount (): void {
    this.props.navigationListener.removeListener(this.onStepNavigation)
  }

  componentDidUpdate (): void {
  }

  /** At this stage 'next' holds the behavior of navigating, usually next but could be 'back'.
   * When the user clicks 'Next', the navigation listener is called and it calls whatever
   * listeners it has, passing them the navigatin behavior in 'next'. In this component, this
   * 'onStepNavigation' function is added as a listener so this is called first. This processes the
   * payment and then calls 'next' to navigate. However, for Apple Pay, the payment is triggered by
   * clicking the dedicated button, so the payment is processed there and the NavigationListener
   * is called ad-hoc in there, triggering the listeners being called.In that case, this funciton is callled
   * but the flag to skip is set to true, and only 'next' is called such that
   * the navigating behavior takes place and therefre the stepper moves forward
   */
  private onStepNavigation = (action: string, next: Function) => {
    if (action === 'Next') {
      if (this.preferredPaymentMethod) {
        this.props.onChange(this.preferredPaymentMethod.id)
        next()
      } else if (this.skipManualToken) {
        console.log("Processing via Apple Pay", "next() is", next, "navigating to next step")
        next()
      } else {
        console.log("onStepNavigation -- authorizing")
        // tokenize payment method
        loadAcceptJs()
          .then(acceptJs => {
            let paymentData: {type: 'card', cardData: CreditCardDataType} | {type: 'bank', bankData: BankAccountDataType} | undefined = undefined

            let last4 = ''
            let description = ''
            let cardType: string | undefined

            if (this.paymentMethodFormType === 'card') {
              if (this.cardData.zipCode.trim().length < 1) {
                DialogManager.show({
                  title: 'Error',
                  content: <div>
                    Billing zip code is required
                  </div>,
                })
                return
              }

              paymentData = {
                type: 'card',
                cardData: {
                  cardNumber: this.cardData.cardNumber,
                  month: this.cardData.expMonth,
                  year: this.cardData.expYear,
                  zip: this.cardData.zipCode,
                  cardCode: this.cardData.securityCode,
                  fullName: this.cardData.nameOnCard,
                },
              }

              const numberValidation = cardValidator.number(this.cardData.cardNumber)

              if (numberValidation.card) {
                switch (numberValidation.card.type) {
                  case 'visa':
                    description = 'Visa'
                    cardType = '001'
                    break;
                  case 'mastercard':
                    description = 'MasterCard'
                    cardType = '002'
                    break;
                  case 'american-express':
                    description = 'American Express'
                    cardType = '003'
                    break;
                  case 'discover':
                    description = 'Discover'
                    cardType = '004'
                    break;
                  default:
                    description = 'Credit/Debit Card'
                    break;
                }
              } else {
                description = 'Credit/Debit Card'
              }

              last4 = this.cardData.cardNumber.substr(this.cardData.cardNumber.length - 4)
            } else if (this.paymentMethodFormType === 'bank') {
              paymentData = {
                type: 'bank',
                bankData: {
                  accountType: 'checking',
                  nameOnAccount: this.bankData.nameOnAccount,
                  routingNumber: this.bankData.routingNumber,
                  accountNumber: this.bankData.accountNumber,
                },
              }

              description = 'Checking'
              last4 = this.bankData.accountNumber.substr(this.bankData.accountNumber.length - 4)
            }

            if (paymentData) {
              if (this.selectedNewPaymentMethod?.tokenizer.type === 'Accept.js') {
                const authData: AcceptJsAuthData = {
                  apiLoginID: this.selectedNewPaymentMethod.tokenizer.authentication.name,
                  clientKey: this.selectedNewPaymentMethod.tokenizer.authentication.clientKey,
                }

                let extraData = {}

                if (this.paymentMethodFormType === 'bank') {
                  extraData = {
                    ...extraData,
                    address: this.bankData.address,
                    city: this.bankData.city,
                    state: this.bankData.state,
                    zipCode: this.bankData.zipCode,
                    nameOnCard: this.cardData.nameOnCard,
                  }
                }

                return acceptJs.tokenizePaymentMethod(authData, paymentData)
                  .then((response: AcceptJsResponseDataType) => {
                    ApiClientFactory.getInstance()
                      .post(this.paymentMethodData!.postback.postbackUrl, {
                        type: this.paymentMethodFormType,
                        last4: last4,
                        description: description,
                        token: response.opaqueData,
                        ...extraData,
                      })
                      .then(response => {
                        this.props.onChange(response.data.preferredPaymentMethod)
                        next()
                      })
                      .catch(err => {
                        let extendedError: string | undefined = undefined
                        if (err.isAxiosError && err.response.status === 400 && _.isString(err.response.data)) {
                          extendedError = err.response.data
                        }

                        DialogManager.show({
                          title: 'Error',
                          content: `There was an error handling your payment method.${extendedError ? ` ${extendedError}` : ''}`,
                        })
                      })
                  })
                  .catch((response: AcceptJsResponseDataType) => {
                    const errors = response.messages.message.map(message => message.text)

                    DialogManager.show({
                      title: 'Error',
                      content: <div>
                        There was an error with the payment processor
                        {errors.map((e, idx) => <div key={idx}>{e}</div>)}
                      </div>,
                    })
                  })
              } else if (this.selectedNewPaymentMethod?.tokenizer.type === 'CyberSource') {
                const authData: CyberSourceKey = {
                  kid: this.selectedNewPaymentMethod.tokenizer.authentication.jwk.kid,
                  keystore: this.selectedNewPaymentMethod.tokenizer.authentication.jwk,
                }

                const csTokenizer = paymentData.type === 'card'
                  ? (new CyberSourceClient()).tokenizePaymentMethod(authData, {
                    cardType: cardType ?? '',
                    cardNumber: paymentData.cardData.cardNumber,
                    securityCode: paymentData.cardData.cardCode || '',
                    expiryMonth: paymentData.cardData.month,
                    expiryYear: paymentData.cardData.year,
                  }).then(response => ({
                    type: 'card',
                    data: response,
                  }))
                  : new Promise((resolve, reject) => {
                    const bankData = (paymentData as {bankData: BankAccountDataType}).bankData
                    const data = {
                      accountType: bankData.accountType,
                      accountNumber: bankData.accountNumber,
                      nameOnAccount: bankData.nameOnAccount,
                      routingNumber: bankData.routingNumber,
                    }
                    resolve({
                      type: 'bank',
                      data,
                    })
                  })

                return csTokenizer.then((response: any) => {
                  let extraData = {}

                  if (paymentData?.type === 'card') {
                    extraData = {
                      ...extraData,
                      expMonth: paymentData.cardData.month,
                      expYear: paymentData.cardData.year,
                      zipCode: this.cardData.zipCode,
                      nameOnCard: this.cardData.nameOnCard,
                    }
                  } else if (paymentData?.type === 'bank') {
                    extraData = {
                      ...extraData,
                      address: this.bankData.address,
                      city: this.bankData.city,
                      state: this.bankData.state,
                      zipCode: this.bankData.zipCode,
                    }
                  }

                  ApiClientFactory.getInstance()
                    .post(this.paymentMethodData!.postback.postbackUrl, {
                      type: this.paymentMethodFormType,
                      last4: last4,
                      description: description,
                      token: response,
                      ...extraData,
                    })
                    .then(response => {
                      this.props.onChange(response.data.preferredPaymentMethod)
                      next()
                    })
                    .catch(err => {
                      let extendedError: string | undefined = undefined
                      if (err.isAxiosError && err.response.status === 400 && _.isString(err.response.data)) {
                        extendedError = err.response.data
                      }

                      DialogManager.show({
                        title: 'Error',
                        content: `There was an error handling your payment method.${extendedError ? ` ${extendedError}` : ''}`,
                      })
                    })
                })
                  .catch((err: any) => {
                    DialogManager.show({
                      title: 'Error',
                      content: <div>
                        There was an error with the payment processor
                      </div>,
                    })

                  })
              } else if (this.selectedNewPaymentMethod?.tokenizer.type === 'CyberSourceMicroform') {
                const options = {
                  expirationMonth: this.cardData.expMonth.padStart(2, '0'),
                  expirationYear: this.cardData.expYear,
                }

                this.microform.createToken(options, (err: any, transientToken: string) => {
                  if (err) {
                    DialogManager.show({
                      title: 'Error',
                      content: <div>{err.message || 'There was an error with the payment processor'}</div>
                    })
                    return
                  }


                  // Decode transient token to get card details
                  try {
                    const payload = JSON.parse(atob(transientToken.split('.')[1]))
                    const cardInfo = payload.content.paymentInformation.card.number
                    last4 = cardInfo.maskedValue.slice(-4)
                    const detectedTypes = cardInfo.detectedCardTypes || []
                    const typeCode = detectedTypes[0] // e.g. "003"

                    switch (typeCode) {
                      case '001': description = 'Visa'; break
                      case '002': description = 'MasterCard'; break
                      case '003': description = 'American Express'; break
                      case '004': description = 'Discover'; break
                      default: description = 'Credit/Debit Card'
                    }
                  } catch (e) {
                    last4 = '****'
                    description = 'Credit/Debit Card'
                  }

                  ApiClientFactory.getInstance().post(this.paymentMethodData!.postback.postbackUrl, {
                    type: this.paymentMethodFormType,
                    last4: last4,
                    description: description,
                    token: transientToken,  // this is the new token format //TODO wrap in object and add tokenType
                    expMonth: this.cardData.expMonth,
                    expYear: this.cardData.expYear,
                    zipCode: this.cardData.zipCode,
                    nameOnCard: this.cardData.nameOnCard,
                  }).then(response => {
                    this.props.onChange(response.data.preferredPaymentMethod);
                    next();
                  }).catch(err => {
                    DialogManager.show({
                      title: 'Error',
                      content: <div>{err.message || 'There was an error processing the payment'}</div>
                    })
                  });
                });
              }
            } else {
              // invalid payment type
            }
          })
          .catch(() => DialogManager.show({
            title: 'Error',
            content: 'There was an error loading the payment processor interface',
          }))
      }

      return true
    } else {
      return false
    }
  }

  private loadPaymentMethodData = () => {
    this.loading = true
    this.preferredPaymentMethodRemoved = false

    return ApiClient.getInstance().get(this.props.url)
      .then(response => {
        this.paymentMethodData = response.data
        this.paymentMethodData?.newPaymentMethods.forEach(pm => pm.uuid = uuidv4().toString())
        this.selectedNewPaymentMethodUuid = this.paymentMethodData!.newPaymentMethods.length ? this.paymentMethodData!.newPaymentMethods[0].uuid : undefined
      })
      .catch(error => this.error = 'There was an error loading the payment method data')
      .then(() => {
        // loading must flip before the microform load starts: render() only mounts the
        // #number-container/#securityCode-container divs once loading is false, and
        // injectMicroformFields needs them to already exist (see docs/local-payment-domain-dev-setup.md
        // "Known issues" — the microform load used to be chained ahead of this flag flip,
        // which made a fast/cached CDN load race the container's own render and self-seal
        // into a permanent error state).
        this.loading = false

        const microformMethod = this.paymentMethodData?.newPaymentMethods.find(npm => npm.tokenizer?.type === 'CyberSourceMicroform')
        if (!microformMethod || this.microform) return

        return loadMicroform(microformMethod.tokenizer, MICROFORM_STYLES)
          .then(mf => {
            this.microform = mf
            return this.injectMicroformFields()
          })
          .catch(() => console.error('Microform failed to load'))
      })
  }

  private renderPaymentForm = () => {
    const paymentMethodData = this.paymentMethodData
    if (this.preferredPaymentMethod) {
      return this.renderSavedPaymentMethod(this.preferredPaymentMethod)
    } else if (paymentMethodData && paymentMethodData.newPaymentMethods.length) {
      return <div>
        <ButtonGroup color="primary" role="radiogroup" aria-label="Payment Method Type">
          {
            paymentMethodData.newPaymentMethods.map(pm => <Button
              key={pm.method}
              size="small"
              variant="contained"
              role="radio"
              aria-checked={pm.uuid === this.selectedNewPaymentMethod?.uuid}
              color={pm.uuid === this.selectedNewPaymentMethod?.uuid ? 'primary' : 'inherit'}
              onClick={() => this.selectedNewPaymentMethodUuid = pm.uuid}
            >{pm.name}</Button>)
          }
        </ButtonGroup>
        <div>
          {
            this.selectedNewPaymentMethod
              ? this.renderPaymentMethodForm(this.selectedNewPaymentMethod.method)
              : null
          }
        </div>
      </div>
    } else {
      return 'No payment methods'
    }
  }

  private renderSavedPaymentMethod = (paymentMethod: SavedPaymentMethodType) => {
    return <Card>
      <CardContent style={{ paddingBottom: 16 }}>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1 }}>
            <div><b>{paymentMethod.displayDetails.label}</b></div>
            {
              paymentMethod.displayDetails.subLabel
                ? <div>{paymentMethod.displayDetails.subLabel}</div>
                : null
            }
          </div>
          <div style={{ paddingLeft: 6 }}>
            <DynamicIcon icon={paymentMethod.displayDetails.icon} style={{ width: 60 }}/>
          </div>
        </div>
        <Button
          color="primary"
          onClick={() => this.preferredPaymentMethodRemoved = true}
        >{paymentMethod.displayDetails.removeLabel}</Button>
      </CardContent>
    </Card>
  }

  private renderPaymentMethodForm = (type: string) => {
    const showMicroform = this.paymentMethodFormType === 'card' && this.selectedNewPaymentMethod?.tokenizer.type === 'CyberSourceMicroform';
    const showRegularCard = this.paymentMethodFormType === 'card' && this.selectedNewPaymentMethod?.tokenizer.type !== 'CyberSourceMicroform';
    const showBank = this.paymentMethodFormType === 'bank';

    return (
      <>
        {/* Microform card - always rendered, hidden via CSS */}
        <div style={{ display: showMicroform ? 'block' : 'none' }}>
          <Grid container spacing={2} style={{ marginTop: 10 }}>
          <Grid item xs={12}>
            <FormGroup row>
              <StyledInput
                  id="nameoncard"
                  name="nameoncard"
                  fullWidth
                  value={this.cardData.nameOnCard}
                  onChange={ev => this.cardData.nameOnCard = ev.target.value}
                  placeholder="Name on Card"
                  startAdornment={<InputAdornment position="start"><AccountBox style={{ color: '#999' }} /></InputAdornment>}
                  inputProps={{
                    autoComplete: 'cc-name',
                    "aria-label": "Name on Card"
                  }}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <FormGroup row>
              <div style={{width: '100%', position: 'relative'}}>
                <div id="number-container" className="form-control microform-field"></div>
                <CreditCard style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999',
                  pointerEvents: 'none',
                  zIndex: 10,
                  fontSize: 24
                }}/>
              </div>
            </FormGroup>
          </Grid>
          <Grid item xs={6}>
            <FormGroup row>
              <Autocomplete
                  options={['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']}
                  id="cc-exp-month"
                  style={{width: '100%'}}
                  inputValue={this.cardData.expMonth}
                  onInputChange={(event, newInputValue) => this.cardData.expMonth = newInputValue}
                  renderInput={(params) => {
                    const props = {
                      ...params,
                      InputProps: {
                        ...(params.InputProps),
                        startAdornment: <InputAdornment position="start">
                          <CalendarToday style={{ color: '#999' }}/>
                        </InputAdornment>,
                        inputProps: {
                          ...(params.inputProps),
                          autoComplete: 'cc-exp-month',
                          'aria-label': "Two-digit Expiraton Month"
                        },
                      },
                    }

                    return (
                        <StyledTextField
                            variant="standard"
                            fullWidth
                            type="number"
                            {...props}
                            placeholder="Exp. MM"
                            margin="none" />
                    );
                  }}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={6}>
            <FormGroup row>
              <Autocomplete
                  options={_.range(moment().year(), moment().year() + 11).map(y => String(y))}
                  id="cc-exp-year"
                  style={{ width: '100%' }}
                  inputValue={this.cardData.expYear}
                  onInputChange={(event, newInputValue) => this.cardData.expYear = newInputValue}
                  renderInput={(params) => {
                    const props = {
                      ...params,
                      InputProps: {
                        ...(params.InputProps),
                        startAdornment: <InputAdornment position="start">
                          <CalendarToday style={{ color: '#999' }}/>
                        </InputAdornment>,
                        inputProps: {
                          ...(params.inputProps),
                          autoComplete: 'cc-exp-year',
                          'aria-label': "Four-digit Expiraton Year"
                        },
                      },
                    }

                    return (
                        <StyledTextField
                            variant="standard"
                            fullWidth
                            type="number"
                            {...props}
                            placeholder="YYYY"
                            margin="none" />
                    );
                  }}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={6}>
            <FormGroup row>
              <div style={{width: '100%', position: 'relative'}}>
                <div id="securityCode-container" className="form-control microform-field"></div>
                <Lock style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999',
                  pointerEvents: 'none',
                  zIndex: 10,
                  fontSize: 24
                }}/>
              </div>
            </FormGroup>
          </Grid>
          <Grid item xs={6}>
            <FormGroup row>
              <StyledInput
                  id="postal-code"
                  name="postal-code"
                  inputProps={{
                    autoComplete: 'shipping postal-code',
                    "aria-label": "Zip code"
                  }}
                  startAdornment={<InputAdornment position="start"><LocationOn style={{ color: '#999' }}/></InputAdornment>}
                  placeholder="Zip Code"
                  fullWidth
                  value={this.cardData.zipCode}
                  onChange={ev => this.cardData.zipCode = ev.target.value}
              />
            </FormGroup>
          </Grid>
        </Grid>
        </div>

        {/* Regular card - conditionally rendered */}
        {showRegularCard && (
            <Grid container spacing={2} style={{ marginTop: 10 }}>
              <Grid item xs={12}>
                <FormGroup row>
                  <StyledInput
                      id="nameoncard"
                      name="nameoncard"
                      fullWidth
                      value={this.cardData.nameOnCard}
                      onChange={ev => this.cardData.nameOnCard = ev.target.value}
                      placeholder="Name on Card"
                      startAdornment={<InputAdornment position="start"><CreditCard style={{ color: '#999' }}/></InputAdornment>}
                      inputProps={{
                        autoComplete: 'cc-name',
                        "aria-label": "Name on Card"
                      }}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12}>
                <FormGroup row>
                  <StyledInput
                      id="cardnumber"
                      name="cardnumber"
                      fullWidth
                      value={this.cardData.cardNumber}
                      onChange={ev => this.cardData.cardNumber = ev.target.value}
                      placeholder="Card Number"
                      startAdornment={<InputAdornment position="start"><AccountBox style={{ color: '#999' }}/></InputAdornment>}
                      inputProps={{
                        autoComplete: 'cc-number',
                        'aria-label': "Card Number"
                      }}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={6}>
                <FormGroup row>
                  <Autocomplete
                      options={['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']}
                      id="cc-exp-month"
                      style={{ width: '100%' }}
                      inputValue={this.cardData.expMonth}
                      onInputChange={(event, newInputValue) => this.cardData.expMonth = newInputValue}
                      renderInput={(params) => {
                        const props = {
                          ...params,
                          InputProps: {
                            ...(params.InputProps),
                            startAdornment: <InputAdornment position="start">
                              <CalendarToday style={{ color: '#999' }}/>
                            </InputAdornment>,
                            inputProps: {
                              ...(params.inputProps),
                              autoComplete: 'cc-exp-month',
                              'aria-label': "Two-digit Expiraton Month"
                            },
                          },
                        }

                        return (
                            <StyledTextField
                                variant="standard"
                                fullWidth
                                type="number"
                                {...props}
                                placeholder="Exp. MM"
                                margin="none" />
                        );
                      }}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={6}>
                <FormGroup row>
                  <Autocomplete
                      options={_.range(moment().year(), moment().year() + 11).map(y => String(y))}
                      id="cc-exp-year"
                      style={{ width: '100%' }}
                      inputValue={this.cardData.expYear}
                      onInputChange={(event, newInputValue) => this.cardData.expYear = newInputValue}
                      renderInput={(params) => {
                        const props = {
                          ...params,
                          InputProps: {
                            ...(params.InputProps),
                            startAdornment: <InputAdornment position="start">
                              <CalendarToday style={{ color: '#999' }}/>
                            </InputAdornment>,
                            inputProps: {
                              ...(params.inputProps),
                              autoComplete: 'cc-exp-year',
                              'aria-label': "Four-digit Expiraton Year"
                            },
                          },
                        }

                        return (
                            <StyledTextField
                                variant="standard"
                                fullWidth
                                type="number"
                                {...props}
                                placeholder="YYYY"
                                margin="none" />
                        );
                      }}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={6}>
                <FormGroup row>
                  <StyledInput
                      id="cvv2"
                      name="cvv2"
                      inputProps={{
                        autoComplete: 'cc-csc',
                        "aria-label": "Security Code"
                      }}
                      startAdornment={<InputAdornment position="start"><Lock style={{ color: '#999' }}/></InputAdornment>}
                      placeholder="CVV"
                      fullWidth
                      value={this.cardData.securityCode}
                      onChange={ev => this.cardData.securityCode = ev.target.value}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={6}>
                <FormGroup row>
                  <StyledInput
                      id="postal-code"
                      name="postal-code"
                      inputProps={{
                        autoComplete: 'shipping postal-code',
                        "aria-label": "Zip code"
                      }}
                      startAdornment={<InputAdornment position="start"><LocationOn style={{ color: '#999' }}/></InputAdornment>}
                      placeholder="Zip Code"
                      fullWidth
                      value={this.cardData.zipCode}
                      onChange={ev => this.cardData.zipCode = ev.target.value}
                  />
                </FormGroup>
              </Grid>
            </Grid>
        )}

        {/* Bank form - conditionally rendered */}
        {showBank && (
          <Grid container spacing={2} style={{ marginTop: 10 }}>
        <Grid item xs={12}>
          <FormGroup row>
            <StyledInput
              placeholder="Name on Bank Account"
              startAdornment={
                <InputAdornment position="start">
                  <AccountBox style={{ color: '#999' }}/>
                </InputAdornment>
              }
              fullWidth
              value={this.bankData.nameOnAccount}
              onChange={ev => this.bankData.nameOnAccount = ev.target.value}
            />
          </FormGroup>
        </Grid>
        <Grid item xs={12}>
          <FormGroup row>
            <StyledInput
              placeholder="Routing Number"
              startAdornment={
                <InputAdornment position="start">
                  <AccountBalance style={{ color: '#999' }}/>
                </InputAdornment>
              }
              fullWidth
              value={this.bankData.routingNumber}
              onChange={ev => this.bankData.routingNumber = ev.target.value}
            />
          </FormGroup>
        </Grid>
        <Grid item xs={12}>
          <FormGroup row>
            <StyledInput
              placeholder="Account Number"
              startAdornment={
                <InputAdornment position="start">
                  <AccountBox style={{ color: '#999' }}/>
                </InputAdornment>
              }
              fullWidth
              value={this.bankData.accountNumber}
              onChange={ev => this.bankData.accountNumber = ev.target.value}
            />
          </FormGroup>
        </Grid>
        <Grid item xs={12}>
          <FormGroup row>
            <StyledInput
              placeholder="Address"
              startAdornment={
                <InputAdornment position="start">
                  <HomeOutlined style={{ color: '#999' }}/>
                </InputAdornment>
              }
              fullWidth
              value={this.bankData.address}
              onChange={ev => this.bankData.address = ev.target.value}
            />
          </FormGroup>
        </Grid>
        <Grid item xs={6}>
          <FormGroup row>
            <StyledInput
              placeholder="City"
              fullWidth
              value={this.bankData.city}
              onChange={ev => this.bankData.city = ev.target.value}
            />
          </FormGroup>
        </Grid>
        <Grid item xs={2}>
          <FormGroup row>
            <StyledInput
              placeholder="State"
              fullWidth
              value={this.bankData.state}
              onChange={ev => this.bankData.state = ev.target.value}
            />
          </FormGroup>
        </Grid>
        <Grid item xs={4}>
          <FormGroup row>
            <StyledInput
              placeholder="Zip"
              fullWidth
              value={this.bankData.zipCode}
              onChange={ev => this.bankData.zipCode = ev.target.value}
            />
          </FormGroup>
        </Grid>
      </Grid>
        )}
      </>
    );
  }

  render () {
    if (this.loading) return <CircularProgress/>
    if (this.error) return <div>{this.error}</div>

    return (
      <>
        <div style={{display: this.showPaymentForm ? 'none' : undefined}}>
          <PaymentMethodSelector
            digitalMethods={this.paymentMethodData?.newDigitalMethods}
            onCheckoutWithCard={() => this.showPaymentForm = true}
            onCheckoutWithAppleApprovedByUser={this.handleApplePayCheckout.bind(this)}
            onCheckoutWithGoogleApprovedByUser={this.handleGooglePayCheckout.bind(this)}
          />
        </div>
        <div style={{display: this.showPaymentForm ? undefined : 'none'}}>
          {this.renderPaymentForm()}
        </div>
      </>
    )
  }

  private handleApplePayCheckout(payment: any) {
    this.skipManualToken = true

    const displayName = payment?.token?.paymentMethod?.displayName || ''
    const description = displayName.replace(/[\d\s]+$/, '').trim()
    const digits = displayName.replace(/\D/g, '')
    const last4 = digits.slice(-4)

    const billing = payment?.billingContact || {}

    const payload = {
      type: 'applepay',
      method: 'applepay',
      description,
      nameOnCard: [billing.givenName, billing.familyName].filter(Boolean).join(' '),
      last4,
      expMonth: '',
      expYear: '',
      address: (billing.addressLines || []).join(', '),
      city: billing.locality || '',
      state: billing.administrativeArea || '',
      zipCode: billing.postalCode || '',
      eMail: billing.emailAddress || '',
      token: payment?.token,
    }

    return ApiClientFactory.getInstance().post(this.paymentMethodData!.postback.postbackUrl, payload).then(response => {
      this.props.onChange(response.data.preferredPaymentMethod);
      this.props.navigationListener.handleNavigationButton("Next")
      return true
    }).catch(err => {
      // Reset skipManualToken so a failed Apple Pay attempt doesn't permanently skip
      // manual tokenization on the next "Next" (onStepNavigation's skipManualToken branch),
      // which would lock the user out of card entry. On the success path it stays set —
      // there is nothing left to tokenize once Apple Pay has completed.
      this.skipManualToken = undefined
      DialogManager.show({
        title: 'Error',
        content: <div>{err.message || 'There was an error processing the payment'}</div>
      })
      return false;
    });
  }

  private handleGooglePayCheckout(token: string) {
    this.skipManualToken = true
    ApiClientFactory.getInstance().post(this.paymentMethodData!.postback.postbackUrl, {
      type: 'googlepay',
      method: 'googlepay',
      description: 'Google Pay',
      last4: '',
      token: token,
    }).then(response => {
      this.props.onChange(response.data.preferredPaymentMethod);
      this.props.navigationListener.handleNavigationButton("Next")
      return true
    }).catch(err => {
      DialogManager.show({
        title: 'Error',
        content: <div>{err.message || 'There was an error processing the payment'}</div>
      })
      return false;
    });
  }

  // render() only mounts the container divs once `loading` is false, which happens in the
  // same tick this starts loading — but a cached/fast CDN fetch can still beat React's
  // commit. Wait for the containers rather than assume they're already there.
  private injectMicroformFields(attempt = 0) {
    if (this.fieldsLoaded) return

    const numberEl = document.querySelector('#number-container')
    const securityCodeEl = document.querySelector('#securityCode-container')
    if (!numberEl || !securityCodeEl) {
      if (attempt >= 30) {
        console.error('Microform containers never rendered')
        return
      }
      requestAnimationFrame(() => this.injectMicroformFields(attempt + 1))
      return
    }

    this.fieldsLoaded = true
    this.microform.createField('number', { placeholder: 'Enter card number' }).load(numberEl)
    this.microform.createField('securityCode', { placeholder: 'CVV' }).load(securityCodeEl)
  }
});

export default DPaymentMethod
